import type {
  ToolCallMessageContent,
  ToolResultMessageContent,
} from "schemas/chat";
import type { ChatMessage } from "schemas/generated/chatMessage";

import React, { useEffect, useState } from "react";

import { BotIcon } from "@replo/design-system/icons/animated/BotIcon";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { FileTextIcon } from "@replo/design-system/icons/animated/FileTextIcon";
import { GalleryVerticalEndIcon } from "@replo/design-system/icons/animated/GalleryVerticalEndIcon";
import { LinkIcon } from "@replo/design-system/icons/animated/LinkIcon";
import { PenToolIcon } from "@replo/design-system/icons/animated/PenToolIcon";
import { RouteIcon } from "@replo/design-system/icons/animated/RouteIcon";
import { ScanTextIcon } from "@replo/design-system/icons/animated/ScanTextIcon";
import { SearchIcon } from "@replo/design-system/icons/animated/SearchIcon";
import { SquarePenIcon } from "@replo/design-system/icons/animated/SquarePenIcon";
import { WorkflowIcon } from "@replo/design-system/icons/animated/WorkflowIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import { MarkdownRenderer } from "@v2/features/chat/components/chat-elements/chat-message/MarkdownRenderer";
import { useChatId } from "@v2/features/chat/hooks/useChatId";
import { useToolCallProgressForChat } from "@v2/features/chat/store/ChatStoreContext";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { ChevronDown, Search } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const formatDefaultToolDropdownContent = ({
  messages,
}: {
  messages?: ChatMessage[];
}) => {
  const textMessages = messages?.flatMap((message) => {
    return message.content
      .filter((content) => content.type === "text")
      .filter((content) => {
        return (
          !content.isHidden && content.contentType !== "codeChangesOverview"
        );
      })
      .map((content) => ({ ...content, messageId: message.id }));
  });

  if (!textMessages || textMessages.length === 0) {
    return undefined;
  }

  return (
    <div className="flex flex-col gap-2">
      {textMessages.map((message) => (
        <MarkdownRenderer key={message.messageId}>
          {message.text}
        </MarkdownRenderer>
      ))}
    </div>
  );
};

const formatAssetSearchToolDropdownContent = ({
  toolCallId,
  toolResult,
  messages,
}: {
  toolCallId: string;
  toolResult: ToolResultMessageContent | null;
  messages?: ChatMessage[];
}) => {
  if (!messages || messages.length === 0) {
    return undefined;
  }

  const items: React.ReactNode[] = [];
  const messagesContent = messages.flatMap((message) => message.content);

  messagesContent.forEach((content) => {
    if (
      content.type === "tool-call" &&
      content.toolName === "assetSearchTool" &&
      toolCallId === content.toolCallId
    ) {
      const searchTerm = content.input?.searchTerm ?? "unknown";
      items.push(
        <span>
          Searched for <Search size={12} className="inline-block" />{" "}
          <span className="font-mono">{searchTerm}</span>
        </span>,
      );

      if (toolResult) {
        const assets =
          toolResult.output?.type === "json" &&
          "assets" in toolResult.output.value
            ? toolResult.output.value.assets ?? []
            : [];

        items.push(
          <span className="flex flex-col gap-1">
            <span>Found {assets.length} relevant assets</span>

            {assets.length > 0 && (
              <div className="grid grid-cols-4 gap-3 mr-1">
                {assets.map((asset, index) => (
                  <img
                    key={index}
                    src={asset.url}
                    alt={asset.name}
                    className="rounded shadow-xs aspect-square w-full object-cover"
                  />
                ))}
              </div>
            )}
          </span>,
        );
      }
    } else if (content.type === "text") {
      items.push(<MarkdownRenderer>{content.text}</MarkdownRenderer>);
    }
  });

  if (items.length === 0) {
    return undefined;
  }

  return <div className="flex flex-col gap-1">{items}</div>;
};

const formatGetInspirationToolDropdownContent = ({
  toolCallId,
  toolResult,
  messages,
}: {
  toolCallId: string;
  toolResult: ToolResultMessageContent | null;
  messages?: ChatMessage[];
}) => {
  if (!messages || messages.length === 0) {
    return undefined;
  }

  const items: React.ReactNode[] = [];
  const messagesContent = messages.flatMap((message) => message.content);

  messagesContent.forEach((content) => {
    if (
      content.type === "tool-call" &&
      content.toolName === "getInspirationTool" &&
      toolCallId === content.toolCallId
    ) {
      const searchTerm = content.input?.searchTerm ?? "unknown";
      items.push(
        <span>
          Searched for <Search size={12} className="inline-block" />{" "}
          <span className="font-mono">{searchTerm}</span>
        </span>,
      );

      if (toolResult) {
        const imageUrl =
          toolResult.output?.type === "json" &&
          "imageUrl" in toolResult.output.value
            ? toolResult.output.value.imageUrl
            : null;

        items.push(
          <span className="flex flex-col gap-1 items-start">
            <span>
              {imageUrl
                ? "Found a reference image"
                : "No reference image found"}
            </span>

            {imageUrl && (
              <img
                src={imageUrl}
                alt="Reference image"
                className="rounded flex-shrink-0 shadow-xs object-contain"
              />
            )}
          </span>,
        );
      }
    } else if (content.type === "text") {
      items.push(<MarkdownRenderer>{content.text}</MarkdownRenderer>);
    }
  });

  if (items.length === 0) {
    return undefined;
  }

  return <div className="flex flex-col gap-1">{items}</div>;
};

const ToolBlock = ({
  icon,
  message,
  dropdownContent,
  isActivelyStreaming = false,
}: {
  icon: React.ReactNode;
  message: string;
  dropdownContent?: React.ReactNode;
  isActivelyStreaming?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(isActivelyStreaming);
  const isDebug = shouldShowV2Debuggers();

  // NOTE (Ben, 2025-11-19): Auto-expand when streaming starts, collapse when done
  useEffect(() => {
    setIsExpanded(isActivelyStreaming);
  }, [isActivelyStreaming]);

  return (
    <motion.div
      className={twMerge(
        "flex flex-col gap-1 w-full",
        dropdownContent && "cursor-pointer",
      )}
      onClick={() => {
        if (dropdownContent) {
          setIsExpanded((prev) => !prev);
        }
      }}
    >
      <div className="flex flex-row items-center gap-1.5 w-full">
        {icon}

        <span className="text-sm">{message}</span>

        {dropdownContent && (
          <div
            className={twMerge(
              isDebug
                ? "ml-auto h-6 w-6 flex items-center justify-center"
                : "flex items-center justify-center w-6 ml-auto",
            )}
          >
            <ChevronDown
              size={14}
              className={twMerge(
                "transition-transform flex-shrink-0",
                isExpanded && "rotate-180",
              )}
            />
          </div>
        )}
      </div>

      <AnimatePresence>
        {isExpanded && dropdownContent && (
          <motion.div
            className="pl-5 text-placeholder text-sm"
            style={{
              backgroundImage: "none",
              backgroundClip: "none",
              WebkitBackgroundClip: "none",
              WebkitTextFillColor: "initial",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            {dropdownContent}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const EditToolBlock = ({
  toolCall,
  toolResult,
  messages,
  isActivelyStreaming = false,
}: {
  toolCall: ToolCallMessageContent;
  toolResult: ToolResultMessageContent | null;
  messages?: ChatMessage[];
  isActivelyStreaming?: boolean;
}) => {
  const chatId = useChatId();
  const progress = useToolCallProgressForChat(chatId, toolCall.toolCallId);

  const getMessage = () => {
    if (toolResult) {
      return "Generated changes";
    }
    if (
      !progress ||
      progress.toolName !== "editTool" ||
      progress.changes.lineCount === 0
    ) {
      return "Generating changes...";
    }
    const linesChanged = progress.changes.lineCount;
    if (linesChanged === 1) {
      return "Generating 1 change...";
    }
    return `Generating ${linesChanged} changes...`;
  };

  const message = getMessage();

  return (
    <ToolBlock
      message={message}
      icon={
        toolResult ? (
          <CircleCheckIcon size={14} animateOnMount />
        ) : (
          <PenToolIcon size={14} loop />
        )
      }
      dropdownContent={formatDefaultToolDropdownContent({ messages })}
      isActivelyStreaming={isActivelyStreaming}
    />
  );
};

export default function ToolCallRenderer({
  toolCall,
  toolResult,
  messages,
  isActivelyStreaming = false,
}: {
  toolCall: ToolCallMessageContent;
  toolResult: ToolResultMessageContent | null;
  messages?: ChatMessage[];
  isActivelyStreaming?: boolean;
}) {
  return exhaustiveSwitch({
    type: toolCall.toolName,
  })({
    staticAnalysisTool: () => (
      <ToolBlock
        message={toolResult ? "Verified changes" : "Verifying changes..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <BotIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    editTool: () => (
      <EditToolBlock
        toolCall={toolCall}
        toolResult={toolResult}
        messages={messages}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    readTool: () => (
      <ToolBlock
        message={toolResult ? "Read files" : "Reading files..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <ScanTextIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    lsTool: () => (
      <ToolBlock
        message={toolResult ? "Found files" : "Finding files..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <FileTextIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    planWriteTool: () => (
      <ToolBlock
        message={toolResult ? "Updated plan" : "Updating plan..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <RouteIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    todoWriteTool: () => (
      <ToolBlock
        message={toolResult ? "Updated to-dos" : "Updating to-dos..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <SquarePenIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    // NOTE (Ben, 2025-11-17, REPL-20213): We don't render these tools because they have special UI handled in the MessageGroup component
    suggestTemplatesTool: () => null,
    generatePageTool: () => null,
    getIntegrationConfigTool: () => null,
    inputProcessorTool: () => (
      <ToolBlock
        message={toolResult ? "Processed input" : "Processing input..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <ScanTextIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    assetSearchTool: () => (
      <ToolBlock
        message={toolResult ? "Retrieved assets" : "Retrieving assets..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <GalleryVerticalEndIcon size={14} loop />
          )
        }
        dropdownContent={formatAssetSearchToolDropdownContent({
          toolCallId: toolCall.toolCallId,
          toolResult,
          messages,
        })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    URLTool: () => (
      <ToolBlock
        message={toolResult ? "Visited URL" : "Visiting URL..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <LinkIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    getInspirationTool: () => (
      <ToolBlock
        message={
          toolResult
            ? "Retrieved reference images"
            : "Retrieving reference images..."
        }
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <GalleryVerticalEndIcon size={14} loop />
          )
        }
        dropdownContent={formatGetInspirationToolDropdownContent({
          toolCallId: toolCall.toolCallId,
          toolResult,
          messages,
        })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    productSearchTool: () => (
      <ToolBlock
        message={toolResult ? "Searched products" : "Searching products..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <SearchIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    replicationTool: () => (
      <ToolBlock
        message={toolResult ? "Replication complete" : "Replicating page..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <BotIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    globTool: () => (
      <ToolBlock
        message={toolResult ? "Found files" : "Finding files..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <FileTextIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    grepTool: () => (
      <ToolBlock
        message={toolResult ? "Found matches" : "Searching code..."}
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <SearchIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
    activatePageGenModeTool: () => (
      <ToolBlock
        message={
          toolResult
            ? "Activated page generation mode"
            : "Activating page generation mode..."
        }
        icon={
          toolResult ? (
            <CircleCheckIcon size={14} animateOnMount />
          ) : (
            <WorkflowIcon size={14} loop />
          )
        }
        dropdownContent={formatDefaultToolDropdownContent({ messages })}
        isActivelyStreaming={isActivelyStreaming}
      />
    ),
  });
}
